/** Editing actions shared by the library, inspector, timeline and keyboard shortcuts. */

import {
  clipDuration,
  DEFAULTS,
  elementTimes,
  frameRect,
  makeAudioClip,
  makeElement,
  merge,
  sceneIndexAt,
  sortedScenes,
  type Asset,
  type CameraKey,
  type Element,
  type Project,
  type Scene,
  type ShapeProps,
  type TextProps,
} from "@studio/engine";
import { selectedElements, setSelectedElements, uid, useEditor } from "./store";

export function sceneAt(p: Project, t: number): Scene {
  const scenes = sortedScenes(p);
  return scenes[sceneIndexAt(scenes, t)];
}

function placement(p: Project, t: number) {
  const scene = sceneAt(p, t);
  const fr = frameRect(p, scene);
  return { scene, start: Math.max(0, +(t - scene.start).toFixed(3)), cx: fr.x + fr.w / 2, cy: fr.y + fr.h / 2, fr };
}

function nextZ(p: Project): number {
  return p.elements.reduce((m, e) => Math.max(m, e.z), 0) + 1;
}

function add(el: Element) {
  useEditor.getState().edit((d) => {
    d.elements.push(el);
  });
  useEditor.getState().select({ kind: "element", id: el.id });
}

export function addImage(asset: Asset, layerSize?: { w: number; h: number } | null) {
  const { project: p, time } = useEditor.getState();
  if (!p) return;
  const { scene, start, cx, cy, fr } = placement(p, time);
  const h = layerSize?.h ?? asset.height ?? 1000;
  const w = layerSize?.w ?? asset.width ?? 1000;
  const scale = Math.min((fr.h * 0.7) / h, (fr.w * 0.45) / w);
  add(
    makeElement({
      id: uid("e"),
      sceneId: scene.id,
      type: "image",
      name: asset.name.replace(/\.[^.]+$/, ""),
      z: nextZ(p),
      transform: { x: cx - fr.w * 0.18, y: cy, scale: +scale.toFixed(4) },
      start,
      enter: { mode: "draw", outlineDur: 3, fillDur: 1.5 },
      image: { assetId: asset.id, brushScale: 1, groupOrder: null },
    }),
  );
}

export function addText() {
  const { project: p, time } = useEditor.getState();
  if (!p) return;
  const font = p.assets.find((a) => a.kind === "font");
  if (!font) return alert("Upload a font first (TTF/OTF).");
  const { scene, start, cx, cy, fr } = placement(p, time);
  const text: TextProps = merge(DEFAULTS.textProps as TextProps, { content: "Title", fontAssetId: font.id, size: 120 });
  add(
    makeElement({
      id: uid("e"),
      sceneId: scene.id,
      type: "text",
      name: "Text",
      z: nextZ(p),
      transform: { x: cx + fr.w * 0.22, y: cy },
      start,
      enter: { mode: "draw", outlineDur: 1.5 },
      text,
    }),
  );
}

export function addShape(kind: ShapeProps["kind"]) {
  const { project: p, time } = useEditor.getState();
  if (!p) return;
  const { scene, start, cx, cy } = placement(p, time);
  const shape: ShapeProps = merge(DEFAULTS.shapeProps as ShapeProps, {
    kind,
    w: kind === "line" || kind === "arrow" ? 360 : 400,
    h: kind === "line" || kind === "arrow" ? 0 : 240,
    points: kind === "line" || kind === "arrow" ? [{ x: -180, y: 0 }, { x: 180, y: 0 }] : [],
  });
  add(
    makeElement({
      id: uid("e"),
      sceneId: scene.id,
      type: "shape",
      name: kind[0].toUpperCase() + kind.slice(1),
      z: nextZ(p),
      transform: { x: cx, y: cy },
      start,
      enter: { mode: "draw", outlineDur: 1, fillDur: 0.8 },
      shape,
    }),
  );
}

export function addClip(asset: Asset, role: "voice" | "music" = "voice") {
  const { project: p, time } = useEditor.getState();
  if (!p) return;
  const len = asset.duration ?? 0;
  // first track where [time, time+len) is free
  let track = role === "music" ? 10 : 0;
  for (;;) {
    const busy = p.audio.some((c) => c.track === track && c.start < time + len && time < c.start + clipDuration(p, c));
    if (!busy) break;
    track++;
  }
  const clip = makeAudioClip({ id: uid("a"), assetId: asset.id, role, track, start: +time.toFixed(3) });
  useEditor.getState().edit((d) => {
    d.audio.push(clip);
  });
  useEditor.getState().select({ kind: "clip", id: clip.id });
}

export function deleteSelection() {
  const { selection, edit } = useEditor.getState();
  if (!selection) return;
  const many = selectedElements();
  if (many.length > 1) {
    edit((d) => void (d.elements = d.elements.filter((e) => !many.includes(e.id))));
    useEditor.getState().select(null);
    return;
  }
  if (selection.kind === "scene") return deleteScene(selection.id);
  edit((d) => {
    if (selection.kind === "element") d.elements = d.elements.filter((e) => e.id !== selection.id);
    else if (selection.kind === "clip") d.audio = d.audio.filter((c) => c.id !== selection.id);
    else if (selection.kind === "marker") d.markers = d.markers.filter((m) => m.id !== selection.id);
    else if (selection.kind === "camkey") d.camera.keyframes = d.camera.keyframes.filter((k) => k.id !== selection.id);
  });
  useEditor.getState().select(null);
}

// ------------------------------------------------------------------------------------- scenes

/** New scene starting at the playhead, framed like the scene it splits. */
export function addScene() {
  const { project: p, time, edit, select } = useEditor.getState();
  if (!p) return;
  const fps = p.output.fps;
  const t = Math.round(time * fps) / fps;
  const cur = sceneAt(p, t);
  if (Math.abs(cur.start - t) < 0.1) return alert("A scene already starts here. Move the playhead first.");
  const n = p.scenes.length + 1;
  const scene = merge(DEFAULTS.scene, { id: uid("s"), name: `Scene ${n}`, start: t, frame: cur.frame ? { ...cur.frame } : null });
  edit((d) => {
    d.scenes.push(scene);
    d.scenes.sort((a, b) => a.start - b.start);
    // elements that start after the split move into the new scene (same absolute time)
    for (const el of d.elements) {
      if (el.sceneId === cur.id && cur.start + el.start >= t) {
        el.sceneId = scene.id;
        el.start = +(cur.start + el.start - t).toFixed(4);
      }
    }
  });
  select({ kind: "scene", id: scene.id });
}

export function deleteScene(id: string) {
  const { project: p, edit, select } = useEditor.getState();
  if (!p) return;
  if (p.scenes.length <= 1) return alert("A project needs at least one scene.");
  const scenes = sortedScenes(p);
  const i = scenes.findIndex((s) => s.id === id);
  const into = scenes[i > 0 ? i - 1 : 1];
  const gone = scenes[i];
  edit((d) => {
    for (const el of d.elements) {
      if (el.sceneId !== id) continue;
      el.sceneId = into.id;
      el.start = Math.max(0, +(gone.start + el.start - into.start).toFixed(4));
    }
    d.scenes = d.scenes.filter((s) => s.id !== id);
    if (i === 0) d.scenes.find((s) => s.id === into.id)!.start = 0;
  });
  select(null);
}

// ------------------------------------------------------------------------------------- markers & camera

export function addMarker(t?: number) {
  const st = useEditor.getState();
  if (!st.project) return;
  const fps = st.project.output.fps;
  const time = Math.round((t ?? st.time) * fps) / fps;
  const n = st.project.markers.length + 1;
  st.edit((d) => {
    d.markers.push({ id: uid("m"), t: time, label: `M${n}` });
    d.markers.sort((a, b) => a.t - b.t);
  });
}

export function addCameraKey(cam: { x: number; y: number; zoom: number; rotation: number }) {
  const st = useEditor.getState();
  if (!st.project) return;
  const key: CameraKey = { id: uid("k"), t: +st.time.toFixed(3), x: round1(cam.x), y: round1(cam.y), zoom: +cam.zoom.toFixed(3), rotation: +cam.rotation.toFixed(1), easing: "easeInOut", source: "manual" };
  st.edit((d) => {
    d.camera.keyframes.push(key);
  });
  st.select({ kind: "camkey", id: key.id });
}

/** Turn an auto-camera key into a manual one with the same id (it then overrides the auto key). */
export function overrideAutoKey(k: CameraKey) {
  const st = useEditor.getState();
  st.edit((d) => {
    if (!d.camera.keyframes.some((x) => x.id === k.id)) d.camera.keyframes.push({ ...k, source: "manual" });
  });
  st.select({ kind: "camkey", id: k.id });
}

const round1 = (v: number) => Math.round(v * 10) / 10;

export function duplicateSelection() {
  const { selection, project: p, edit } = useEditor.getState();
  if (!selection || !p) return;
  const many = selectedElements();
  if (many.length > 1) {
    let z = nextZ(p);
    const copies = p.elements.filter((e) => many.includes(e.id)).map((el) => ({ ...structuredClone(el), id: uid("e"), z: z++, transform: { ...el.transform, x: el.transform.x + 40, y: el.transform.y + 40 } }));
    edit((d) => void d.elements.push(...copies));
    setSelectedElements(copies.map((c) => c.id));
    return;
  }
  if (selection.kind === "element") {
    const el = p.elements.find((e) => e.id === selection.id);
    if (!el) return;
    const copy: Element = { ...structuredClone(el), id: uid("e"), z: nextZ(p), transform: { ...el.transform, x: el.transform.x + 40, y: el.transform.y + 40 } };
    edit((d) => {
      d.elements.push(copy);
    });
    useEditor.getState().select({ kind: "element", id: copy.id });
  } else {
    const c = p.audio.find((x) => x.id === selection.id);
    if (!c) return;
    const copy = { ...c, id: uid("a"), start: c.start + clipDuration(p, c) };
    edit((d) => {
      d.audio.push(copy);
    });
    useEditor.getState().select({ kind: "clip", id: copy.id });
  }
}

export function reorder(id: string, dir: "up" | "down" | "top" | "bottom") {
  const { project: p, edit } = useEditor.getState();
  if (!p) return;
  const sorted = [...p.elements].sort((a, b) => a.z - b.z);
  const i = sorted.findIndex((e) => e.id === id);
  if (i < 0) return;
  const [el] = sorted.splice(i, 1);
  const j = dir === "top" ? sorted.length : dir === "bottom" ? 0 : dir === "up" ? Math.min(sorted.length, i + 1) : Math.max(0, i - 1);
  sorted.splice(j, 0, el);
  edit((d) => {
    sorted.forEach((e, k) => {
      const t = d.elements.find((x) => x.id === e.id);
      if (t) t.z = k;
    });
  });
}

/** Element start in absolute time (for timeline/inspector display). */
export function absStart(p: Project, el: Element): number {
  return elementTimes(p).get(el.id)?.enter0 ?? 0;
}

// ------------------------------------------------------------------------------------- clipboard

const CLIP_KEY = "studio:clipboard";

interface ClipboardData {
  project: string;
  elements: (Element & { abs: number })[];
}

/** Copy the selected elements (with their absolute start times) to the app clipboard. */
export function copySelection(cut = false): number {
  const { project: p } = useEditor.getState();
  if (!p) return 0;
  const ids = selectedElements();
  if (!ids.length) return 0;
  const times = elementTimes(p);
  const data: ClipboardData = {
    project: p.id,
    elements: p.elements.filter((e) => ids.includes(e.id)).map((e) => ({ ...structuredClone(e), abs: times.get(e.id)!.enter0 })),
  };
  try {
    localStorage.setItem(CLIP_KEY, JSON.stringify(data));
  } catch {
    /* storage blocked: clipboard is per-tab only */
  }
  memoryClip = data;
  if (cut) useEditor.getState().edit((d) => void (d.elements = d.elements.filter((e) => !ids.includes(e.id))));
  return data.elements.length;
}

let memoryClip: ClipboardData | null = null;

/** Paste at the playhead into its scene, keeping relative timing; `inPlace` keeps positions. */
export function paste(inPlace = false): string {
  const { project: p, time } = useEditor.getState();
  if (!p) return "";
  let data = memoryClip;
  try {
    const raw = localStorage.getItem(CLIP_KEY);
    if (raw) data = JSON.parse(raw);
  } catch {
    /* use the in-memory copy */
  }
  if (!data?.elements.length) return "Nothing to paste.";
  const assets = new Set(p.assets.map((a) => a.id));
  const usable = data.elements.filter((e) => (!e.image || assets.has(e.image.assetId)) && (!e.text || assets.has(e.text.fontAssetId)));
  if (!usable.length) return "The copied images aren't in this project.";
  const scene = sceneAt(p, time);
  const first = Math.min(...usable.map((e) => e.abs));
  let z = nextZ(p);
  const fresh: Element[] = usable.map(({ abs, ...e }) => ({
    ...e,
    id: uid("e"),
    sceneId: scene.id,
    z: z++,
    start: Math.max(0, +(time - scene.start + (abs - first)).toFixed(4)),
    transform: inPlace ? e.transform : { ...e.transform, x: e.transform.x + 30, y: e.transform.y + 30 },
  }));
  useEditor.getState().edit((d) => void d.elements.push(...fresh));
  setSelectedElements(fresh.map((e) => e.id));
  return usable.length < data.elements.length ? `Pasted ${usable.length}; ${data.elements.length - usable.length} use images that aren't in this project.` : "";
}

// ------------------------------------------------------------------------------------- align

type Bounds = { x: number; y: number; w: number; h: number };

/** Align the selected elements by their board bounds. */
export function align(how: "left" | "hcenter" | "right" | "top" | "vmiddle" | "bottom", boundsOf: (id: string) => Bounds | null) {
  const ids = selectedElements();
  const bs = ids.map((id) => [id, boundsOf(id)] as const).filter((x): x is readonly [string, Bounds] => !!x[1]);
  if (bs.length < 2) return;
  const L = Math.min(...bs.map(([, b]) => b.x));
  const R = Math.max(...bs.map(([, b]) => b.x + b.w));
  const T = Math.min(...bs.map(([, b]) => b.y));
  const B = Math.max(...bs.map(([, b]) => b.y + b.h));
  useEditor.getState().edit((d) => {
    for (const [id, b] of bs) {
      const el = d.elements.find((e) => e.id === id)!;
      if (how === "left") el.transform.x += L - b.x;
      if (how === "right") el.transform.x += R - (b.x + b.w);
      if (how === "hcenter") el.transform.x += (L + R) / 2 - (b.x + b.w / 2);
      if (how === "top") el.transform.y += T - b.y;
      if (how === "bottom") el.transform.y += B - (b.y + b.h);
      if (how === "vmiddle") el.transform.y += (T + B) / 2 - (b.y + b.h / 2);
      el.transform.x = Math.round(el.transform.x * 10) / 10;
      el.transform.y = Math.round(el.transform.y * 10) / 10;
    }
  });
}

/** Spread the selected elements so the gaps between them are equal. */
export function distribute(axis: "h" | "v", boundsOf: (id: string) => Bounds | null) {
  const bs = selectedElements()
    .map((id) => [id, boundsOf(id)] as const)
    .filter((x): x is readonly [string, Bounds] => !!x[1])
    .sort((a, b) => (axis === "h" ? a[1].x - b[1].x : a[1].y - b[1].y));
  if (bs.length < 3) return;
  const size = (b: Bounds) => (axis === "h" ? b.w : b.h);
  const pos = (b: Bounds) => (axis === "h" ? b.x : b.y);
  const start = pos(bs[0][1]);
  const end = pos(bs[bs.length - 1][1]) + size(bs[bs.length - 1][1]);
  const gap = (end - start - bs.reduce((a, [, b]) => a + size(b), 0)) / (bs.length - 1);
  let cursor = start;
  useEditor.getState().edit((d) => {
    for (const [id, b] of bs) {
      const el = d.elements.find((e) => e.id === id)!;
      const delta = cursor - pos(b);
      if (axis === "h") el.transform.x = Math.round((el.transform.x + delta) * 10) / 10;
      else el.transform.y = Math.round((el.transform.y + delta) * 10) / 10;
      cursor += size(b) + gap;
    }
  });
}

/** Ctrl+A: every element of the scene at the playhead. */
export function selectScene() {
  const { project: p, time } = useEditor.getState();
  if (!p) return;
  const sc = sceneAt(p, time);
  setSelectedElements(p.elements.filter((e) => e.sceneId === sc.id && !e.hidden).map((e) => e.id));
}

/** Files nothing in the project refers to (no layer, clip, hand, background or caption font). The
 *  project's first font stays: it is the default for new text and captions. */
export function unusedAssets(p: Project): Asset[] {
  const ids = new Set(p.assets.map((a) => a.id));
  const used = new Set<string>();
  const walk = (v: unknown) => {
    if (typeof v === "string") {
      if (ids.has(v)) used.add(v);
    } else if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === "object") Object.values(v).forEach(walk);
  };
  const { assets: _assets, ...rest } = p;
  walk(rest);
  const firstFont = p.assets.find((a) => a.kind === "font");
  return p.assets.filter((a) => !used.has(a.id) && a !== firstFont);
}

/** Take unused files out of the project (undoable). Their files stay on the server while undo
 *  or the project's history can still bring them back; "purge" frees the ones that can't. */
export function removeAssets(remove: Asset[]) {
  const gone = new Set(remove.map((a) => a.id));
  useEditor.getState().edit((d) => {
    d.assets = d.assets.filter((a) => !gone.has(a.id));
  });
}

export interface SpreadOptions {
  columns: number;
  /** space between frames, as a fraction of the frame size */
  gap: number;
  /** turn the transition into each new frame into a camera pan */
  pan: boolean;
}

/** Scenes that share a frame: a scene and the "build" scenes that add to its drawing. */
export function frameGroups(p: Project): Scene[][] {
  const groups: Scene[][] = [];
  for (const s of sortedScenes(p)) {
    if (s.startMode === "build" && groups.length) groups[groups.length - 1].push(s);
    else groups.push([s]);
  }
  return groups;
}

export const defaultColumns = (groups: number) => Math.max(1, Math.ceil(Math.sqrt(groups)));

/** Give every frame group its own place on the board, in reading order snaking row by row (so
 *  the camera never jumps across the board), and move each scene's layers and manual camera keys
 *  with it. The board grows to fit. Undoable. */
export function spreadScenes(opts: SpreadOptions) {
  useEditor.getState().edit((d) => {
    const groups = frameGroups(d as Project);
    const W = d.output.width;
    const H = d.output.height;
    const cols = Math.max(1, Math.min(opts.columns, groups.length));
    const gx = Math.round(W * opts.gap);
    const gy = Math.round(H * opts.gap);
    const rows = Math.ceil(groups.length / cols);
    const scenes = new Map(d.scenes.map((s) => [s.id, s]));
    const ordered = sortedScenes(d as Project);
    groups.forEach((g, k) => {
      const row = Math.floor(k / cols);
      const col = row % 2 === 0 ? k % cols : cols - 1 - (k % cols);
      const old = frameRect(d as Project, g[0]);
      const frame = { x: col * (W + gx), y: row * (H + gy), w: old.w, h: old.h };
      const dx = frame.x - old.x;
      const dy = frame.y - old.y;
      const ids = new Set(g.map((s) => s.id));
      for (const el of d.elements) {
        if (!ids.has(el.sceneId)) continue;
        el.transform.x += dx;
        el.transform.y += dy;
      }
      // manual camera keys inside this group's time move with its drawing
      const t0 = g[0].start;
      const next = ordered[ordered.indexOf(g[g.length - 1]) + 1];
      const t1 = next ? next.start : Infinity;
      for (const key of d.camera.keyframes) {
        if (key.t >= t0 && key.t < t1) {
          key.x += dx;
          key.y += dy;
        }
      }
      for (const s of g) scenes.get(s.id)!.frame = { ...frame };
      if (opts.pan && k > 0) {
        const s = scenes.get(g[0].id)!;
        s.transition = { ...s.transition, type: "pan", duration: Math.max(1, s.transition.type === "cut" ? 0 : s.transition.duration) };
      }
    });
    d.board.width = Math.max(d.board.width, cols * W + (cols - 1) * gx);
    d.board.height = Math.max(d.board.height, rows * H + (rows - 1) * gy);
  });
}

/**
 * Ripple edit: everything in `base` that happens at or after time `at` moves by `dt`, so the
 * rest of the video keeps its timing relative to the change: later scenes (their layers follow),
 * audio clips of the same role (voice, or music when a music clip is dragged), layers of the scene
 * the point falls in that start after it, markers, manual camera keys and caption cues. The first
 * scene always starts at 0; when the point is inside it, its later layers move instead. Writes
 * into the draft `d` from the untouched `base`, so it can be re-run while dragging.
 */
export function rippleShift(d: Project, base: Project, at: number, dt: number, role: "voice" | "music" = "voice") {
  const eps = 1e-6;
  const after = (t: number) => t >= at - eps;
  const r4 = (t: number) => +t.toFixed(4);
  const scenes = sortedScenes(base);
  const moves = (s: Scene) => after(s.start) && s.start > eps;
  const host = [...scenes].reverse().find((s) => !moves(s));
  const bs = new Map(base.scenes.map((s) => [s.id, s]));
  for (const s of d.scenes) if (moves(bs.get(s.id)!)) s.start = r4(bs.get(s.id)!.start + dt);
  const be = new Map(base.elements.map((e) => [e.id, e]));
  if (host)
    for (const e of d.elements) {
      const b = be.get(e.id)!;
      if (b.sceneId === host.id && after(host.start + b.start)) e.start = r4(b.start + dt);
    }
  const ba = new Map(base.audio.map((c) => [c.id, c]));
  for (const c of d.audio) {
    const b = ba.get(c.id)!;
    if (b.role === role && after(b.start)) c.start = r4(b.start + dt);
  }
  const bm = new Map(base.markers.map((m) => [m.id, m]));
  for (const m of d.markers) if (after(bm.get(m.id)!.t)) m.t = r4(bm.get(m.id)!.t + dt);
  const bk = new Map(base.camera.keyframes.map((k) => [k.id, k]));
  for (const k of d.camera.keyframes) if (after(bk.get(k.id)!.t)) k.t = r4(bk.get(k.id)!.t + dt);
  const bc = new Map(base.subtitles.map((c) => [c.id, c]));
  for (const c of d.subtitles) {
    const b = bc.get(c.id)!;
    if (after(b.t0)) {
      c.t0 = r4(b.t0 + dt);
      c.t1 = r4(b.t1 + dt);
    }
  }
  if (base.duration != null && base.duration >= at - eps) d.duration = r4(base.duration + dt);
}

/** How far a ripple at `at` may pull things earlier (a negative number): moved scenes stay after
 *  the scene before them, and nothing goes before 0 or before the start of its scene. */
export function rippleMin(base: Project, at: number, role: "voice" | "music" = "voice"): number {
  const eps = 1e-6;
  const scenes = sortedScenes(base);
  const moving = scenes.filter((s) => s.start >= at - eps && s.start > eps);
  const host = [...scenes].reverse().find((s) => !moving.includes(s));
  let lo = -at;
  if (moving.length && host) lo = Math.max(lo, host.start + 0.2 - moving[0].start);
  for (const c of base.audio) if (c.role === role && c.start >= at - eps) lo = Math.max(lo, -c.start);
  if (host)
    for (const e of base.elements)
      if (e.sceneId === host.id && host.start + e.start >= at - eps) lo = Math.max(lo, -e.start);
  return Math.min(0, lo);
}
